import React, { useState } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { Appointment } from '../types/appointment.types';

import AppointmentsList from './AppointmentsList';
import ViewAppointmentDialog from './ViewAppointmentDialog';
import { useSnackbar } from 'notistack';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AppointmentsViewProps {
  appointments: Appointment[];
  loading: boolean;
  error: Error | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  };
  onViewAppointment: (appointment: Appointment) => void;
  onStatusUpdate?: (updatedAppointment: Appointment) => void;
}

const AppointmentsView: React.FC<AppointmentsViewProps> = ({
  appointments,
  loading,
  error,
  pagination,

  onViewAppointment,
  onStatusUpdate
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  
  const handleViewAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedAppointment(null);
  };

  const handleViewClick = (appointment: Appointment) => {
    onViewAppointment(appointment);
    handleViewAppointment(appointment);
  };

  const handleStatusAction = (action: string) => {
    // Handle status actions here
    enqueueSnackbar(`Action '${action}' triggered`, { 
      variant: 'info' as const,
      autoHideDuration: 3000,
      anchorOrigin: {
        vertical: 'top' as const,
        horizontal: 'right' as const,
      },
    });
  };

  // Show loading state
  if (loading && appointments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          {error.message || 'Failed to load appointments'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <AppointmentsList 
        appointments={appointments}
        loading={loading}
        error={error}
        pagination={{
          page: pagination.page || 0, 
          limit: pagination.limit || 10,
          total: pagination.total || 0,
          totalPages: pagination.totalPages || 1,
          onPageChange: (event, newPage) => {
            if (pagination.onPageChange) {
              pagination.onPageChange(event, newPage); 
            }
          },
          onRowsPerPageChange: (event) => {
            if (pagination.onRowsPerPageChange) {
              pagination.onRowsPerPageChange(event);
            }
          }
        }}
        onViewAppointment={handleViewClick}
      />
      
      {/* View Appointment Dialog */}
      {selectedAppointment && (
        <ViewAppointmentDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          appointment={selectedAppointment}
          onStatusAction={handleStatusAction}
          onStatusUpdate={(updatedAppointment) => {
            // Notify parent component about the status update
            if (onStatusUpdate) {
              onStatusUpdate(updatedAppointment);
            }
            // Close the dialog after a short delay
            setTimeout(handleCloseDialog, 500);
          }}
        />
      )}
    </Box>
  );
};

export default AppointmentsView;
